import { PARSER_CONFIG } from './constants';
import { HTMLParser, PlainTextParser, SpaceIndentedParser, SpreadsheetParser, WordDocParser } from './parsers';
import { InputSizeError, ParseError, Parser, TreeNode, TreeNodesParserConfig } from './types';

/**
 * Main parser class that orchestrates different format parsers
 */
export class TreeNodesParser {
  private readonly parsers: readonly Parser[] = Object.freeze([
    new SpreadsheetParser(),
    new HTMLParser(),
    new WordDocParser(),
    new SpaceIndentedParser(),
    new PlainTextParser()
  ]);

  private readonly maxInputSize: number;

  /**
   * Create a new TreeNodesParser instance
   * @param config Optional configuration object
   */
  constructor(config: TreeNodesParserConfig = {}) {
    // Handle edge cases for maxInputSize
    const configuredSize = config.maxInputSize;
    if (configuredSize !== undefined && configuredSize < 0) {
      // Negative values default to standard limit
      this.maxInputSize = PARSER_CONFIG.maxInputSize;
    } else {
      // 0 and positive values are used as-is, undefined falls back to default
      this.maxInputSize = configuredSize ?? PARSER_CONFIG.maxInputSize;
    }
  }

  /**
   * Parse input string into hierarchical tree structure
   * @param input The string to parse
   * @returns Array of TreeNode objects representing the hierarchy
   * @throws ParseError if parsing fails
   * @throws InputSizeError if input exceeds size limit
   */
  parse(input: string): TreeNode[] {
    if (!input?.trim()) {
      return [];
    }

    // Check input size limit
    if (input.length > this.maxInputSize) {
      throw new InputSizeError(
        `Input size ${input.length} exceeds maximum allowed size of ${this.maxInputSize} characters`,
        input.length,
        this.maxInputSize
      );
    }

    for (const parser of this.parsers) {
      try {
        if (parser.canParse(input)) {
          if (process.env.NODE_ENV === 'development') {
            console.info(`TreeNodesParser: Using ${parser.name} for input parsing`);
          }
          return parser.parse(input);
        }
      } catch (error) {
        if (error instanceof ParseError) {
          throw error;
        }
        throw new ParseError(
          `Unexpected error in ${parser.name}: ${error instanceof Error ? error.message : 'Unknown error'}`,
          parser.name,
          input.substring(0, 100)
        );
      }
    }

    return [];
  }
}
